import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { BlogEnglishNotice } from '@/components/BlogEnglishNotice';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Clock, User, Zap, Users, Building, CheckCircle, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const BlogHubSpotSalesforce = () => {
  const scrollToDemo = () => {
    const currentPath = window.location.pathname;
    const isFrenchPage = currentPath.startsWith('/fr');
    
    // If we're on the home page, scroll to the form
    if (currentPath === '/' || currentPath === '/fr') {
      const element = document.getElementById('cta');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page with language prefix
      const homeUrl = isFrenchPage ? '/fr?scrollTo=cta' : '/?scrollTo=cta';
      window.location.href = homeUrl;
    }
  };

  return (
    <Layout>
      <SEOHead 
        title="HubSpot vs Salesforce AI Integration: Complete 2025 Comparison Guide"
        description="Compare HubSpot and Salesforce AI capabilities, integration options, and voice assistant compatibility. Find the best CRM platform for your sales team in 2025."
        keywords="HubSpot vs Salesforce 2025, CRM comparison, HubSpot Salesforce integration, best CRM platform, AI CRM features, voice assistant CRM"
        canonicalUrl="https://noq5.ai/blog/hubspot-vs-salesforce-ai-integration-2025"
      />
      
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": "HubSpot vs Salesforce AI integration: Complete 2025 comparison guide",
            "description": "Compare HubSpot and Salesforce AI capabilities, integration options, and voice assistant compatibility. Find the best CRM platform for your sales team in 2025.",
            "image": "https://noq5.ai/images/hubspot-vs-salesforce.png",
            "author": {
              "@type": "Organization",
              "name": "NoQ5.ai Team"
            },
            "publisher": {
              "@type": "Organization",
              "name": "NoQ5.ai",
              "logo": {
                "@type": "ImageObject",
                "url": "https://noq5.ai/images/noq5-logo.png"
              }
            },
            "datePublished": "2025-01-15",
            "dateModified": "2025-01-15",
            "mainEntityOfPage": "https://noq5.ai/blog/hubspot-vs-salesforce-ai-integration-2025"
          })
        }}
      />
      
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://noq5.ai/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://noq5.ai/blog"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "HubSpot vs Salesforce AI integration: Complete 2025 comparison guide",
                "item": "https://noq5.ai/blog/hubspot-vs-salesforce-ai-integration-2025"
              }
            ]
          })
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto"
      >
        <Link 
          to="/blog" 
          className="inline-flex items-center text-primary hover:text-primary/80 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blog
        </Link>

        <article className="prose prose-lg max-w-none">
          <header className="mb-8">
            <h1 className="text-4xl font-bold mb-4 text-foreground">
              HubSpot vs Salesforce AI integration: Complete 2025 comparison guide
            </h1>
            <div className="flex items-center gap-4 text-muted-foreground mb-6">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Marketing Team</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>12 min read</span>
              </div>
            </div>
          </header>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-8">
            <h3 className="text-xl font-semibold text-blue-800 mb-4">
              The CRM decision that shapes your sales success
            </h3>
            <p className="text-blue-700">
              Choosing between HubSpot and Salesforce isn't just about features—it's about finding the platform that best integrates with AI tools like voice assistants to maximize your team's productivity. This comprehensive guide compares both platforms' AI capabilities, integration options, and compatibility with modern sales tools.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-foreground">HubSpot vs Salesforce: The core differences</h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-card border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-semibold">HubSpot</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">All-in-one marketing, sales, and service</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">User-friendly interface</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Built-in AI features</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <X className="w-4 h-4 text-red-500" />
                    <span className="text-sm">Limited customization</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <X className="w-4 h-4 text-red-500" />
                    <span className="text-sm">Fewer third-party integrations</span>
                  </div>
                </div>
              </div>

              <div className="bg-card border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Building className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold">Salesforce</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Highly customizable</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Extensive third-party ecosystem</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Advanced reporting and analytics</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <X className="w-4 h-4 text-red-500" />
                    <span className="text-sm">Steep learning curve</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <X className="w-4 h-4 text-red-500" />
                    <span className="text-sm">Requires admin setup</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-foreground">AI capabilities comparison</h2>
            
            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse border border-border rounded-lg">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="border border-border p-4 text-left font-semibold">Feature</th>
                    <th className="border border-border p-4 text-center font-semibold">HubSpot</th>
                    <th className="border border-border p-4 text-center font-semibold">Salesforce</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-4 font-medium">Built-in AI assistant</td>
                    <td className="border border-border p-4 text-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                    </td>
                    <td className="border border-border p-4 text-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="bg-muted/20">
                    <td className="border border-border p-4 font-medium">Voice command support</td>
                    <td className="border border-border p-4 text-center">
                      <X className="w-5 h-5 text-red-500 mx-auto" />
                    </td>
                    <td className="border border-border p-4 text-center">
                      <X className="w-5 h-5 text-red-500 mx-auto" />
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-4 font-medium">Predictive analytics</td>
                    <td className="border border-border p-4 text-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                    </td>
                    <td className="border border-border p-4 text-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="bg-muted/20">
                    <td className="border border-border p-4 font-medium">Lead scoring</td>
                    <td className="border border-border p-4 text-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                    </td>
                    <td className="border border-border p-4 text-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-4 font-medium">Email automation</td>
                    <td className="border border-border p-4 text-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                    </td>
                    <td className="border border-border p-4 text-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="bg-muted/20">
                    <td className="border border-border p-4 font-medium">Third-party AI integrations</td>
                    <td className="border border-border p-4 text-center text-orange-600 font-medium">Limited</td>
                    <td className="border border-border p-4 text-center text-green-600 font-medium">Extensive</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-amber-800 mb-3 flex items-center gap-2">
                <Zap className="w-5 h-5" />
                The AI integration gap
              </h3>
              <p className="text-amber-700">
                Both platforms have built-in AI, but neither offers native voice command support. This is where third-party AI assistants like Oli become crucial—they bridge the gap between natural communication and CRM data entry.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-foreground">Voice assistant integration: Where Oli fits in</h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-card border rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4 text-oli-blue">HubSpot + Oli integration</h3>
                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-oli-blue" />
                    <span className="text-sm">Simple API connection</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-oli-blue" />
                    <span className="text-sm">Works with built-in workflows</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-oli-blue" />
                    <span className="text-sm">Enhances existing AI features</span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm">
                  Perfect for teams wanting voice capabilities without complex setup. Oli complements HubSpot's user-friendly approach.
                </p>
              </div>

              <div className="bg-card border rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4">Salesforce + Oli integration</h3>
                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Deep customization options</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Custom field mapping</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Advanced automation triggers</span>
                  </div>
                </div>
                <p className="text-muted-foreground text-sm">
                  Ideal for enterprise teams needing sophisticated voice-to-CRM workflows with complex business logic.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-foreground">Cost comparison for 2025</h2>
            
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="bg-card border rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Small Team (5 users)</h3>
                <div className="space-y-3">
                  <div>
                    <div className="font-medium">HubSpot Professional</div>
                    <div className="text-2xl font-bold text-orange-600">$500/mo</div>
                    <div className="text-sm text-muted-foreground">+ Oli integration</div>
                  </div>
                  <div>
                    <div className="font-medium">Salesforce Professional</div>
                    <div className="text-2xl font-bold text-blue-600">$375/mo</div>
                    <div className="text-sm text-muted-foreground">+ Setup costs + Oli</div>
                  </div>
                </div>
              </div>

              <div className="bg-card border rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Medium Team (25 users)</h3>
                <div className="space-y-3">
                  <div>
                    <div className="font-medium">HubSpot Enterprise</div>
                    <div className="text-2xl font-bold text-orange-600">$3,600/mo</div>
                    <div className="text-sm text-muted-foreground">All features included</div>
                  </div>
                  <div>
                    <div className="font-medium">Salesforce Enterprise</div>
                    <div className="text-2xl font-bold text-blue-600">$4,000/mo</div>
                    <div className="text-sm text-muted-foreground">+ Admin resources</div>
                  </div>
                </div>
              </div>

              <div className="bg-card border rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Enterprise (100+ users)</h3>
                <div className="space-y-3">
                  <div>
                    <div className="font-medium">HubSpot Enterprise</div>
                    <div className="text-2xl font-bold text-orange-600">$12,000+/mo</div>
                    <div className="text-sm text-muted-foreground">Custom pricing</div>
                  </div>
                  <div>
                    <div className="font-medium">Salesforce Unlimited</div>
                    <div className="text-2xl font-bold text-blue-600">$15,000+/mo</div>
                    <div className="text-sm text-muted-foreground">Maximum flexibility</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-foreground">Which platform is right for your team?</h2>
            
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-orange-800 mb-4">Choose HubSpot if:</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="text-orange-700 space-y-2">
                    <li>• You want an all-in-one solution</li>
                    <li>• Your team values ease of use</li>
                    <li>• You need marketing automation</li>
                    <li>• You have limited technical resources</li>
                  </ul>
                  <ul className="text-orange-700 space-y-2">
                    <li>• You want quick setup and deployment</li>
                    <li>• Your sales process is relatively straightforward</li>
                    <li>• You prefer predictable pricing</li>
                    <li>• You're a small to medium-sized business</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-blue-800 mb-4">Choose Salesforce if:</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="text-blue-700 space-y-2">
                    <li>• You need extensive customization</li>
                    <li>• You have complex sales processes</li>
                    <li>• You want the largest ecosystem of integrations</li>
                    <li>• You have dedicated admin resources</li>
                  </ul>
                  <ul className="text-blue-700 space-y-2">
                    <li>• You're an enterprise organization</li>
                    <li>• You need advanced reporting capabilities</li>
                    <li>• You want maximum scalability</li>
                    <li>• You already use other Salesforce products</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold mb-6 text-foreground">The future of CRM: Voice-first workflows</h2>
            
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold mb-4">Why voice integration matters in 2025</h3>
              <p className="text-muted-foreground mb-4">
                Regardless of whether you choose HubSpot or Salesforce, the future of CRM is voice-first. Both platforms excel at data storage and analysis, but neither solves the fundamental problem of data entry friction.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3">Traditional CRM workflow:</h4>
                  <ol className="text-sm text-muted-foreground space-y-1">
                    <li>1. Attend client meeting</li>
                    <li>2. Take notes on paper/phone</li>
                    <li>3. Return to office</li>
                    <li>4. Remember meeting details</li>
                    <li>5. Navigate CRM interface</li>
                    <li>6. Manually enter data</li>
                    <li>7. Hope nothing was forgotten</li>
                  </ol>
                </div>
                <div>
                  <h4 className="font-semibold mb-3">Voice-first workflow with NoQ5.ai:</h4>
                  <ol className="text-sm space-y-1">
                    <li>1. Attend client meeting</li>
                    <li>2. Speak update to NoQ5</li>
                    <li>3. AI processes and validates</li>
                    <li>4. Data syncs to CRM automatically</li>
                    <li>5. Focus on selling, not data entry</li>
                  </ol>
                </div>
              </div>
            </div>
          </section>

          <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to save 5+ hours a week?</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Try NoQ5.ai, the Salesforce AI Copilot built for reps. Transform how your team updates CRM automatically.
            </p>
            <Button 
              onClick={scrollToDemo}
              size="lg" 
              className="font-semibold"
            >
              Try NoQ5 now →
            </Button>
          </div>

          <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to supercharge your CRM with voice?</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Whether you choose HubSpot or Salesforce, Oli transforms your CRM into a voice-first productivity tool. Book a demo to see how AI voice assistants work with your existing platform.
            </p>
            <Button 
              onClick={scrollToDemo}
              size="lg" 
              className="font-semibold"
            >
              Book a Demo
            </Button>
          </div>
        </article>
      </motion.div>
    </Layout>
  );
};

export default BlogHubSpotSalesforce;